import { FC, useState, useRef } from 'react';
import Text from '@raydiant/raydial/components/Text';
import cn from 'classnames';

interface FileDropperProps {
  className?: string;
  title: string;
  onDrop: (files: FileList) => void;
}

const FileCirclePlus = (
  <svg
    width="52"
    height="46"
    viewBox="0 0 52 46"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.3125 40.9062H25.6484C26.7031 42.5762 28.0215 43.9824 29.6035 45.125C29.3398 45.125 29.0762 45.125 28.8125 45.125H6.3125C3.14844 45.125 0.6875 42.6641 0.6875 39.5V5.75C0.6875 2.67383 3.14844 0.125 6.3125 0.125H20.8145C22.3086 0.125 23.7148 0.740234 24.7695 1.79492L32.7676 9.79297C33.8223 10.8477 34.4375 12.2539 34.4375 13.748V17.6152C32.8555 18.0547 31.4492 18.6699 30.2188 19.5488V14.1875H23.1875C21.6055 14.1875 20.375 12.957 20.375 11.375V4.34375H6.3125C5.52148 4.34375 4.90625 5.04688 4.90625 5.75V39.5C4.90625 40.291 5.52148 40.9062 6.3125 40.9062ZM38.6562 19.8125C43.1387 19.8125 47.2695 22.2734 49.5547 26.1406C51.8398 30.0957 51.8398 34.9297 49.5547 38.7969C47.2695 42.752 43.1387 45.125 38.6562 45.125C34.0859 45.125 29.9551 42.752 27.6699 38.7969C25.3848 34.9297 25.3848 30.0957 27.6699 26.1406C29.9551 22.2734 34.0859 19.8125 38.6562 19.8125ZM40.0625 26.8438C40.0625 26.1406 39.3594 25.4375 38.6562 25.4375C37.8652 25.4375 37.25 26.1406 37.25 26.8438V31.0625H33.0312C32.2402 31.0625 31.625 31.7656 31.625 32.4688C31.625 33.2598 32.2402 33.875 33.0312 33.875H37.25V38.0938C37.25 38.8848 37.8652 39.5 38.6562 39.5C39.3594 39.5 40.0625 38.8848 40.0625 38.0938V33.875H44.2812C44.9844 33.875 45.6875 33.2598 45.6875 32.4688C45.6875 31.7656 44.9844 31.0625 44.2812 31.0625H40.0625V26.8438Z"
      fill="#0047D4"
    />
  </svg>
);

const FileDropper: FC<FileDropperProps> = ({ className, title, onDrop }) => {
  const [isOverDropper, setIsOverDropper] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsOverDropper(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsOverDropper(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsOverDropper(false);
    dragCounter.current = 0;

    const files = e.dataTransfer.files;

    if (files.length > 0) {
      onDrop(files);
    }
  };

  return (
    <div
      className={cn(
        'w-full h-full flex justify-center items-center border-2 border-dashed rounded-xl cursor-pointer transition-all',
        isOverDropper
          ? 'border-blue-600 bg-blue-50'
          : 'border-gray-400 bg-gray-100',
        className,
      )}
      onDragOver={(e) => e.preventDefault()}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
    >
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*,video/*,application/pdf"
        onChange={(e) => e.target.files && onDrop(e.target.files)}
        multiple
      />
      <div className="flex flex-col items-center max-w-[278px] gap-4">
        <div>{FileCirclePlus}</div>
        <div className="text-lg font-regular text-black text-center">
          <Text>{title}</Text>
        </div>
      </div>
    </div>
  );
};

export default FileDropper;
